import React, { useState } from 'react';
import { ENTER_NAME } from '../ISRConstants';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';

type TCreateDialogProps = {
    id: string;
    dialogHeader: string;
    labelName: string;
    value?: string;
    showCancelButton?: boolean;
    actionLabel?: string;
    onClose: () => void;
    onSubmodelFieldSubmit: (submodelName: string) => boolean | void;
    showIndicator?: boolean;
    multiCreate?: boolean;
    entityName?: string;
}

/**
 * The create/rename name prompt, on DS Dialog + TextField + Buttons (§ 88 —
 * the CreateListDialog § 85 recipe). Escape and scrim-click now close through
 * the same `onClose` the X and Cancel use. Unlike the Lists sibling, the
 * `multiCreate` branch is LIVE here (SubModelItem mounts it three times), so
 * its behavior is byte-preserved — including the hand-rolled green
 * "✓ Created" flash, which has no DS Button equivalent (a green-filled
 * disabled control) and deliberately stays as-is.
 *
 * `showIndicator` is the parent's in-flight flag (ISRHomePage passes its
 * `loader`), and since § 209 it also GATES the create buttons
 * (`loading={showIndicator}`): while the parent's API call runs, the buttons
 * wear the DS loading state instead of staying clickable — the double-click
 * that used to fire `createISRProject` twice now reaches no handler. The five
 * mounts that never pass the prop (DefineModel ×3, SubModelItem ×2 — and Edit
 * Model Name, which is sync-local) default to `false` and are byte-identical.
 */
const CreateISRDialog = ({
    id, dialogHeader = "", labelName = "", value,
    onClose, onSubmodelFieldSubmit,
    showCancelButton = true, actionLabel,
    showIndicator = false,
    multiCreate = false, entityName = 'Item'
}: TCreateDialogProps) => {
    const [submodelName, setSubmodelName] = useState(value ?? '');
    const [justCreated, setJustCreated] = useState(false);

    const handleCreateAndAddMore = () => {
        const result = onSubmodelFieldSubmit(submodelName);
        if (result === false) return;
        setSubmodelName("");
        setJustCreated(true);
        setTimeout(() => setJustCreated(false), 2000);
    };

    const handleCreate = () => {
        const result = onSubmodelFieldSubmit(submodelName);
        if (result === false) return;
        toast.success(`${entityName} created successfully`);
        onClose();
    };

    const handleSingleCreate = () => {
        onSubmodelFieldSubmit(submodelName);
        setSubmodelName("");
    };

    /*
        While the parent's create is in flight the dialog is COMMITTED: the API
        is not abortable, and a mid-flight close only hides the dialog — the
        success toast and navigation still land afterwards. One guard covers
        every close route (Cancel, the X, Escape — Dialog routes all three
        through `onClose`); the parent's `finally` always drops the flag, so
        this can never wedge shut.
    */
    const guardedClose = () => {
        if (showIndicator) return;
        onClose();
    };

    const cancelButton = (
        <Button
            id={`${id}-cancel-btn`} data-testid={`${id}-cancel-btn`}
            variant="secondary"
            disabled={showIndicator}
            onClick={guardedClose}
        >
            Cancel
        </Button>
    );

    /*
        Cancel at the far LEFT, the create actions grouped right — the dismissive
        action and the committing ones are different kinds of thing.
        The indicator between them takes `flex-1`, which is what pushes the actions
        right whether or not Cancel renders: `showCancelButton` is false for Edit
        Model Name, and a `justify-between` with a missing first child would have
        slid the actions across to the left instead.
    */
    const footer = (
        <div id={`${id}-footer-row`} className='tw-flex tw-w-full tw-items-center tw-gap-4'>
            {multiCreate || showCancelButton ? cancelButton : null}
            <div id={`${id}-indicator`} className='tw-flex-1 tw-min-w-0'>
                {showIndicator ? (
                    <Typography variant="body-primary" as="span" id={`${id}-indicator-text`} className="tw-text-ink-secondary">
                        Creating your model ...
                    </Typography>
                ) : null}
            </div>
            <div id={`${id}-actions`} className={`tw-flex tw-gap-2 tw-justify-end`}>
                {multiCreate ? (
                    <>
                        {justCreated ? (
                            /* The transient "✓ Created" flash — a live flow here
                               (SubModelItem passes multiCreate). A green-filled
                               disabled control has no DS Button equivalent, so the
                               legacy markup is kept verbatim. */
                            <button data-testid="components-createisrdialog-button-2"
                                id={`${id}-add-more-btn`}
                                disabled
                                className='tw-flex tw-items-center tw-gap-1 tw-bg-[#36B37E] tw-text-white tw-rounded tw-px-3 tw-h-6 tw-text-xs tw-font-medium tw-border-0 tw-cursor-default tw-opacity-100'
                            >
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5.5" stroke="white" strokeWidth="1"/><path d="M3 6L5 8.5L9 3.5" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                {entityName} Created
                            </button>
                        ) : (
                            <Button data-testid="components-createisrdialog-button-3"
                                id={`${id}-add-more-btn`}
                                variant="secondary"
                                disabled={submodelName === ""}
                                loading={showIndicator}
                                onClick={handleCreateAndAddMore}
                            >
                                Create &amp; Add More
                            </Button>
                        )}
                        <Button
                            id={`${id}-submit-btn`} data-testid={`${id}-submit-btn`}
                            variant="primary"
                            disabled={submodelName === ""}
                            loading={showIndicator}
                            onClick={handleCreate}
                        >
                            Create
                        </Button>
                    </>
                ) : (
                    <Button
                        id={`${id}-submit-btn`} data-testid={`${id}-submit-btn`}
                        variant="primary"
                        disabled={submodelName === ""}
                        loading={showIndicator}
                        onClick={handleSingleCreate}
                    >
                        {actionLabel ?? 'Create'}
                    </Button>
                )}
            </div>
        </div>
    );

    return (
        <Dialog isOpen onClose={guardedClose} title={dialogHeader} data-testid={id} footer={footer}>
            <div id={`${id}-form-section`}>
                <TextField
                    label={labelName}
                    value={submodelName}
                    onChange={setSubmodelName}
                    placeholder={ENTER_NAME}
                    id={`${id}-name-input`}
                    data-testid="components-createisrdialog-input-1"
                />
            </div>
        </Dialog>
    );
}

export default CreateISRDialog
